/**
 * Cross-shipment aggregation for the Confirm-deliveries wizard.
 *
 * Campuses that split deliveries by a group dimension receive one shipment
 * PER DELIVERY GROUP (the materializer fans out by the campus policy's
 * `deliverGroupBy` — homeroom, grade, …), so the same item
 * (same portion) arrives split across shipments — physically it's one
 * stack of totes the cafeteria counts ONCE. Aggregate identical lines
 * (same vendor + label + unit + temperature class) into a single display
 * row, keeping the constituent shipment-line refs so a confirmed total can
 * fan back out to the per-shipment receive API. The aggregation key is
 * dimension-agnostic, so whole-school days (one shipment per mealtime)
 * pass through unchanged.
 *
 * Pure (type-only imports) — unit-tested in
 * `client/test/lib/cafeteria/aggregate-deliveries.test.ts`.
 */

import type { CafeteriaDeliveryWire } from '@/api/cafeteria'
import type { TemperatureReadingWire } from '@/api/cafeteria-temperatures'

type DeliveryLineWire = CafeteriaDeliveryWire['lines'][number]

export interface AggregatedDeliveryPart {
  shipmentId: string
  shipmentLineId: string
  /**
   * Catalog identity of the line, carried through so a consumer can join this
   * row to a production-record deliverable by ID rather than by name (PROD-1279
   * spreadsheet mode). OPTIONAL: every field below is additive, so a fixture or
   * a caller built before they existed still type-checks.
   */
  componentId?: string | null
  /** The PACKAGE this line belongs to ("0% Milk"), where the label is the SKU. */
  deliverableName?: string | null
  /** `Component.milkOptionCode` — non-null marks a milk SKU line. */
  milkOptionCode?: string | null
  /**
   * The CHEF's own readings, kept apart from the collapsed `tempDefault` ladder
   * so a consumer can say WHOSE number a box is pre-filled with, and so the
   * kitchen reading can have an input of its own (PROD-1279 spreadsheet mode).
   */
  deliveryTemperatureF?: number | null
  facilityTemperatureF?: number | null
  plannedQuantity: number
  receivedQuantity: number | null
  /** Per-line temp prefill: the cafeteria's receipt reading, else the delivery reading, else the chef's facility reading. */
  tempDefault: number | null
  /** The cafeteria's Delivered count fanned onto this line (server hint); null = not edited, received, or weekly milk. */
  cafeteriaCountedQuantity: number | null
  /** The cafeteria's receipt temperature for this line's row (Temperature logs page); null = not logged, received, or weekly milk. */
  cafeteriaTemperature: TemperatureReadingWire | null
}

export interface AggregatedDeliveryRow {
  /** Stable identity for edit state — survives refetches. */
  key: string
  label: string
  ageRangeNames: string[]
  unitLabel: string
  /** Set when the day spans multiple vendors, so identical labels stay tellable apart. */
  vendorName: string | null
  plannedTotal: number
  /** Prefill for the Received input: sum of (received ?? cafeteria count ?? planned) across parts. */
  receivedDefaultTotal: number
  /** Σ of the parts' cafeteria counts when at least one part carries one (the "Cafeteria counted N" note); else null. */
  cafeteriaCountedTotal: number | null
  /** Prefill for the temp input: first constituent with a reading. */
  tempDefault: number | null
  /** The cafeteria's receipt reading behind that prefill, when one exists (the mark beside the input). */
  cafeteriaTemperature: TemperatureReadingWire | null
  /**
   * HACCP class shared by every constituent line (it is part of the row key).
   * AMBIENT rows take no temperature reading — see `tempCheckRequired`.
   */
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  parts: AggregatedDeliveryPart[]
}

function rowKey(delivery: CafeteriaDeliveryWire, line: DeliveryLineWire): string {
  return [delivery.mealtime, delivery.vendorLocationId, line.label, line.unitLabel, line.temperatureClass ?? ''].join(
    '::',
  )
}

/** A part's prefill: what was received, else the cafeteria's count, else the plan. */
function partReceivedDefault(part: AggregatedDeliveryPart): number {
  return part.receivedQuantity ?? part.cafeteriaCountedQuantity ?? part.plannedQuantity
}

/** Running Σ of the parts' cafeteria counts; stays null until a part carries one. */
function foldCountedTotal(total: number | null, part: AggregatedDeliveryPart): number | null {
  return part.cafeteriaCountedQuantity === null ? total : (total ?? 0) + part.cafeteriaCountedQuantity
}

/**
 * Prefill for the receipt-temperature input: the cafeteria's own receipt
 * reading (the Temperature logs page — it IS the temperature at receipt),
 * else an existing DELIVERY reading, else the chef's FACILITY reading, else a
 * temperature-class-appropriate safe-band default (HOT 165°F / CHILLED 38°F)
 * so the worker confirms rather than re-keys. AMBIENT (and unclassified)
 * lines stay empty — no hold band applies, so there is no honest number to
 * suggest.
 */
export const TEMP_CLASS_PREFILL_F: Partial<Record<string, number>> = {
  HOT: 165,
  CHILLED: 38,
}

/**
 * Whether an item takes a temperature reading at all. AMBIENT (shelf-stable —
 * cereal, condiment packets) has no holding band, so prompting for a reading
 * is noise: those rows render "N/A" and never gate a wizard's submit.
 *
 * Deliberate policy (Sheila, 2026-08-05): the CLASS is trusted as provisioned.
 * `temperatureClass` defaults to AMBIENT in the catalog, so a hot item nobody
 * classified stops prompting — classification hygiene is owned by catalog QA,
 * not worked around here. Unclassified (null) still REQUIRES a reading: null
 * means "unknown", not "shelf-stable".
 */
export function tempCheckRequired(temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null): boolean {
  return temperatureClass !== 'AMBIENT'
}

function lineTempDefault(line: DeliveryLineWire): number | null {
  return (
    line.cafeteriaTemperature?.value ??
    line.deliveryTemperatureF ??
    line.facilityTemperatureF ??
    (line.temperatureClass ? (TEMP_CLASS_PREFILL_F[line.temperatureClass] ?? null) : null)
  )
}

/**
 * Merge every delivery's lines into aggregated rows, first-seen order
 * preserved. Callers group by mealtime BEFORE aggregating (the key still
 * includes the mealtime, so mixing grouped/ungrouped inputs stays safe).
 */
export function aggregateDeliveryRows(deliveries: readonly CafeteriaDeliveryWire[]): AggregatedDeliveryRow[] {
  const vendorIds = new Set(deliveries.map((d) => d.vendorLocationId))
  const byKey = new Map<string, AggregatedDeliveryRow>()
  for (const delivery of deliveries) {
    for (const line of delivery.lines) {
      const key = rowKey(delivery, line)
      const part: AggregatedDeliveryPart = {
        shipmentId: delivery.shipmentId,
        shipmentLineId: line.shipmentLineId,
        componentId: line.componentId,
        deliverableName: line.deliverableName,
        milkOptionCode: line.milkOptionCode,
        deliveryTemperatureF: line.deliveryTemperatureF,
        facilityTemperatureF: line.facilityTemperatureF,
        plannedQuantity: line.plannedQuantity,
        receivedQuantity: line.receivedQuantity,
        tempDefault: lineTempDefault(line),
        cafeteriaCountedQuantity: line.cafeteriaCountedQuantity ?? null,
        cafeteriaTemperature: line.cafeteriaTemperature ?? null,
      }
      const existing = byKey.get(key)
      if (existing) {
        existing.parts.push(part)
        existing.plannedTotal += part.plannedQuantity
        existing.receivedDefaultTotal += partReceivedDefault(part)
        existing.cafeteriaCountedTotal = foldCountedTotal(existing.cafeteriaCountedTotal, part)
        existing.tempDefault = existing.tempDefault ?? part.tempDefault
        existing.cafeteriaTemperature = existing.cafeteriaTemperature ?? part.cafeteriaTemperature
        existing.ageRangeNames = uniqueSorted([...existing.ageRangeNames, ...line.ageRangeNames])
      } else {
        byKey.set(key, {
          key,
          label: line.label,
          ageRangeNames: uniqueSorted(line.ageRangeNames),
          unitLabel: line.unitLabel,
          vendorName: vendorIds.size > 1 ? delivery.vendorLocationName : null,
          plannedTotal: part.plannedQuantity,
          receivedDefaultTotal: partReceivedDefault(part),
          cafeteriaCountedTotal: part.cafeteriaCountedQuantity,
          tempDefault: part.tempDefault,
          cafeteriaTemperature: part.cafeteriaTemperature,
          temperatureClass: line.temperatureClass ?? null,
          parts: [part],
        })
      }
    }
  }
  return [...byKey.values()]
}

/**
 * Fan an aggregated received total back out to the constituent shipment
 * lines, pro-rata by planned quantity (whole units; leftover goes to the
 * earliest parts, deterministically). A zero-planned aggregate splits the
 * total evenly. The parts always sum exactly to `total`.
 */
export function distributeReceivedTotal(
  total: number,
  parts: readonly AggregatedDeliveryPart[],
): Array<{ shipmentId: string; shipmentLineId: string; receivedQuantity: number }> {
  const safeTotal = Math.max(0, Math.round(total))
  const plannedSum = parts.reduce((sum, part) => sum + part.plannedQuantity, 0)
  const weights = parts.map((part) => (plannedSum > 0 ? part.plannedQuantity / plannedSum : 1 / parts.length))
  const floors = weights.map((weight) => Math.floor(safeTotal * weight))
  let remainder = safeTotal - floors.reduce((sum, val) => sum + val, 0)
  return parts.map((part, index) => {
    const extra = remainder > 0 ? 1 : 0
    remainder -= extra
    return {
      shipmentId: part.shipmentId,
      shipmentLineId: part.shipmentLineId,
      receivedQuantity: floors[index]! + extra,
    }
  })
}

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b))
}

/**
 * A line belonging to the campus's WEEKLY MILK drop: the campus is on a fixed
 * weekly milk delivery day AND the line is a milk-stamped component line.
 * Bundled milk never reaches this wire as its own line (only ROOT physical
 * lines are projected), so the stamp alone identifies the weekly drop here.
 */
export function isMilkDeliveryLine(
  delivery: Pick<CafeteriaDeliveryWire, 'schoolMilkDeliveryDayOfWeek'>,
  line: Pick<DeliveryLineWire, 'milkOptionCode'>,
): boolean {
  return delivery.schoolMilkDeliveryDayOfWeek != null && line.milkOptionCode != null
}

/**
 * One per-vendor "Milk" receipt section: the whole weekly milk drop counted
 * as per-SKU rows with a SINGLE temperature check for the group (cartons
 * arrive as one chilled stack — per-mealtime, per-service-day temps would be
 * theater). Rows reuse `AggregatedDeliveryRow` so `distributeReceivedTotal`
 * fans a confirmed SKU total back across the underlying weekly shipments.
 */
export interface MilkReceiptGroup {
  /** `milk::${vendorLocationId}` — stable identity for temp edit state. */
  key: string
  vendorLocationId: string
  /** Set when the day spans multiple vendors, so the section names its dairy. */
  vendorName: string | null
  rows: AggregatedDeliveryRow[]
  /** Prefill for the group temp input: first constituent with a reading. */
  tempDefault: number | null
  /** The cafeteria reading behind that prefill, when one exists. */
  cafeteriaTemperature: TemperatureReadingWire | null
}

/**
 * Split a day's deliveries into meal deliveries (milk lines stripped) and
 * per-vendor milk receipt groups. Milk rows aggregate across ALL mealtimes
 * and shipments — mealtime and temperature class are deliberately out of the
 * row key, because the weekly drop is one physical stack of cartons however
 * many service days it covers. Deliveries left with zero lines drop from
 * `mealDeliveries` (they still exist upstream and still get submitted — the
 * wizard's per-shipment submit loop iterates the ORIGINAL deliveries array).
 * Campuses without a milk schedule pass through untouched.
 */
export function splitMilkDeliveries(deliveries: readonly CafeteriaDeliveryWire[]): {
  mealDeliveries: CafeteriaDeliveryWire[]
  milkGroups: MilkReceiptGroup[]
} {
  const vendorIds = new Set(deliveries.map((d) => d.vendorLocationId))
  const mealDeliveries: CafeteriaDeliveryWire[] = []
  const groups = new Map<string, MilkReceiptGroup>()
  const rowsByKey = new Map<string, AggregatedDeliveryRow>()
  for (const delivery of deliveries) {
    const milkLines = delivery.lines.filter((line) => isMilkDeliveryLine(delivery, line))
    if (milkLines.length === 0) {
      mealDeliveries.push(delivery)
      continue
    }
    const mealLines = delivery.lines.filter((line) => !isMilkDeliveryLine(delivery, line))
    if (mealLines.length > 0) {
      mealDeliveries.push({ ...delivery, lines: mealLines })
    }
    const groupKey = `milk::${delivery.vendorLocationId}`
    let group = groups.get(groupKey)
    if (!group) {
      group = {
        key: groupKey,
        vendorLocationId: delivery.vendorLocationId,
        vendorName: vendorIds.size > 1 ? delivery.vendorLocationName : null,
        rows: [],
        tempDefault: null,
        cafeteriaTemperature: null,
      }
      groups.set(groupKey, group)
    }
    for (const line of milkLines) {
      const key = `${groupKey}::${line.label}::${line.unitLabel}`
      const part: AggregatedDeliveryPart = {
        shipmentId: delivery.shipmentId,
        shipmentLineId: line.shipmentLineId,
        componentId: line.componentId,
        deliverableName: line.deliverableName,
        milkOptionCode: line.milkOptionCode,
        deliveryTemperatureF: line.deliveryTemperatureF,
        facilityTemperatureF: line.facilityTemperatureF,
        plannedQuantity: line.plannedQuantity,
        receivedQuantity: line.receivedQuantity,
        tempDefault: lineTempDefault(line),
        cafeteriaCountedQuantity: line.cafeteriaCountedQuantity ?? null,
        cafeteriaTemperature: line.cafeteriaTemperature ?? null,
      }
      const existing = rowsByKey.get(key)
      if (existing) {
        existing.parts.push(part)
        existing.plannedTotal += part.plannedQuantity
        existing.receivedDefaultTotal += partReceivedDefault(part)
        existing.cafeteriaCountedTotal = foldCountedTotal(existing.cafeteriaCountedTotal, part)
        existing.tempDefault = existing.tempDefault ?? part.tempDefault
        existing.cafeteriaTemperature = existing.cafeteriaTemperature ?? part.cafeteriaTemperature
        existing.ageRangeNames = uniqueSorted([...existing.ageRangeNames, ...line.ageRangeNames])
      } else {
        const row: AggregatedDeliveryRow = {
          key,
          label: line.label,
          ageRangeNames: uniqueSorted(line.ageRangeNames),
          unitLabel: line.unitLabel,
          vendorName: group.vendorName,
          plannedTotal: part.plannedQuantity,
          receivedDefaultTotal: partReceivedDefault(part),
          cafeteriaCountedTotal: part.cafeteriaCountedQuantity,
          tempDefault: part.tempDefault,
          cafeteriaTemperature: part.cafeteriaTemperature,
          // Milk is a cold chain — never ambient, always takes a reading.
          temperatureClass: line.temperatureClass ?? 'CHILLED',
          parts: [part],
        }
        rowsByKey.set(key, row)
        group.rows.push(row)
      }
      group.tempDefault = group.tempDefault ?? part.tempDefault
      group.cafeteriaTemperature = group.cafeteriaTemperature ?? part.cafeteriaTemperature
    }
  }
  return { mealDeliveries, milkGroups: [...groups.values()] }
}
